import { Button, Input, Modal, Select, DatePicker, Pagination,
  notification, message, Tabs, Upload, Spin, Form, Switch
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { orderColumns } from './constants';
import ReportEditor from '../ReportEditor';
import './pacs.css';
import FloatLabel from '../../components/FloatingLabel';
import {
  getUserDetails,
  hasReportingPermission,
  hasStudyViewingPermission,
  makeGetCall,
  makePostCall,
  RADIOLOGY_URL,
} from '../../utils/helper';
import { DISABLE_DEFAULT_FILTERS } from '../../constants/APP_CONSTANTS';
import { useSites } from '../../hooks/useSites';
import { useModalities } from '../../hooks/useModalities';
import { useBodyParts } from '../../hooks/useBodyParts';
import { useWebSocketContext } from '../../context/WebSocketContext';
import { useRegistrationTypes } from '../../hooks/useRegistrationTypes';
import { SavedSearches } from '../orders/constants';
import { getAutoRefreshEnabled, subscribeAutoRefresh } from '../../utils/autoRefreshPubSub';

import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce, throttle } from 'lodash';
import TabPane from 'antd/es/tabs/TabPane';
import TextArea from 'antd/es/input/TextArea';
import ViewNotes from '../ManageNodes/ViewNotes';
import { useForm } from 'antd/es/form/Form';
import OrderInfoPopup from '../OrderInfo';
import PdfViewer from '../../components/PdfViewer';
import { launchViewerOnDualMonitor, checkMonitorSetup } from '../../utils/MonitorManager';
import { SettingOutlined } from '@ant-design/icons';
import PacsAgGrid from '../../components/PacsAgGrid';
import ColumnConfigurator from '../../components/PacsAgGrid/ColumnConfigurator';
import { ColumnConfig, ColumnHeaderActions, PinnedColumnsConfig } from '../../components/PacsAgGrid/types';
import WhatsAppShareModal from '../../components/WhatsAppShareModal';
import DownloadStudyModal from '../../components/DownloadStudyModal';
import EditStudyDetailsModal from '../../components/EditStudyDetailsModal';
import StudyHistoryDialog from '../../components/StudyHistoryDialog';
import TinyEditor from '../ReportEditor/tinymce-editor';
import { FieldLabels } from '../../constants/fieldLabels';

const { RangePicker } = DatePicker;

const PacsList = ({ StyledTabsComponent}) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [reportEditorModal, setReportEditorModal] = useState({ visible: false, data: {} });
  const [saveFiltersModal, setSaveFiltersModal] = useState({ visible: false, data: {} });
  const [assignModal, setAssignModal] = useState({ visible: false, data: {} });
  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState([]);
  const [userList, setUserList] = useState([]);
  const [addFileModal, setAddFileModal] = useState({
    visible: false,
    notes: null,
    modalType: 'upload',
    file: null,
    fileType: null,
  });
  const [viewNotesModal, setViewNotesModal] = useState({ visible: false, details: null });
  const [receiverModal, setReceiverModal] = useState({ visible: false, details: null });
  const [selectedNote, setSelectedNote] = useState({});
  const [orderInfoModal, setOrderInfoModal] = useState({});
  const [viewerModal, setViewerModal] = useState({ visible: false, data: {} });
  const [pdfRefreshing, setPdfRefreshing] = useState(false);
  const [editReportModal, setEditReportModal] = useState({
    visible: false,
    reportData: null,
    patientData: null,
  });

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(getAutoRefreshEnabled());

  const [filterName, setFilterName] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [isUserIdle, setIsUserIdle] = useState(false);

  // Use dynamic configuration hooks
  const { siteOptions, loading: sitesLoading } = useSites();
  const { modalityOptions, loading: modalitiesLoading } = useModalities();
  const { bodyPartOptions, loading: bodyPartsLoading } = useBodyParts();
  const { regTypeOptions, loading: regTypesLoading } = useRegistrationTypes();

  // WebSocket context for real-time updates
  const wsContext = useWebSocketContext();

  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const userDetails = getUserDetails();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [printLoading, setPrintLoading] = React.useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [isLoadingColumnPreferences, setIsLoadingColumnPreferences] = useState(true);
  const [columnConfiguratorVisible, setColumnConfiguratorVisible] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>([]);
  const [pinnedColumns, setPinnedColumns] = useState<PinnedColumnsConfig>({ left: [], right: ['actions_column'] });
  const lastActivityRef = useRef(Date.now());

  const [receiverForm] = useForm();

  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');

  // Set the default value to [yesterday, today] only if DISABLE_DEFAULT_FILTERS is false
  const [dateRange, setDateRange] = useState(DISABLE_DEFAULT_FILTERS ? null : [yesterday, today]);

  const isHOD = userDetails?.user_type === 'hod';

  const userType = userDetails?.user_type;

  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedStudyForShare, setSelectedStudyForShare] = useState(null);

  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedStudyForDownload, setSelectedStudyForDownload] = useState(null);

  const [editStudyModalVisible, setEditStudyModalVisible] = useState(false);
  const [selectedStudyForEdit, setSelectedStudyForEdit] = useState(null);

  // Radiant Viewer state - controlled by props and environment config
  // Only enable if both the prop is true AND the config flag is true
  const radiantAvailable = window.ENV_CONFIG?.RADIANT_AVAILABLE || false;
  const [enableRadiantViewer, setEnableRadiantViewer] = useState(!!radiantAvailable);
  const [studyHistoryModalVisible, setStudyHistoryModalVisible] = useState(false);
  const [selectedStudyForHistory, setSelectedStudyForHistory] = useState(null);

  const handleShareStudy = record => {
    setSelectedStudyForShare(record);
    setShareModalVisible(true);
  };

  const handleDownloadStudy = record => {
    setSelectedStudyForDownload(record);
    setDownloadModalVisible(true);
  };

  const handleEditStudyDetails = record => {
    setSelectedStudyForEdit(record);
    setEditStudyModalVisible(true);
  };

  const openRadiantViewer = studyUID => {
    if (!studyUID) {
      message.error('Study UID not available');
      return;
    }

    // Open Radiant Viewer with the study UID
    const radiantUrl = `radiant://?n=pstv&v=0020000D&v=${studyUID}`;
    window.location.href = radiantUrl;
  };

  const handleShowStudyHistory = record => {
    setSelectedStudyForHistory(record);
    setStudyHistoryModalVisible(true);
  };

  useEffect(() => {
    // getPacsList();
    getSavedFilters();
    getUsersList();
    getQuickFilters();
    loadColumnPreferences();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeAutoRefresh((enabled) => {
      setAutoRefreshEnabled(enabled);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
      setSelectedRowKeys([]); // Clear selection when filters change
    }
    debouncedFilter(filters);
  }, [filters]);


  useEffect(() => {
    let intervalId;

    console.log('inside effect', autoRefreshEnabled, isUserIdle);

    if (autoRefreshEnabled && !isUserIdle) {
      intervalId = setInterval(
        () => {
          // Only refresh if not currently loading and filters are applied
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            // Refresh current page only - maintains user position
            filterResults(filters, pagination.current, pagination.pageSize);
          }
        },
        (window.ENV_CONFIG?.AUTO_REFRESH_INTERVAL_MINUTES || 5) * 60 * 1000
      ); // 5 minutes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, isUserIdle, orders.loading, filters, dateRange]);


  // Subscribe to WebSocket messages for real-time study updates
  useEffect(() => {
    const unsubscribe = wsContext.subscribe((data) => {
      try {

        // Handle study update notifications using state values directly
        if (data.type === 'study_update' && autoRefreshEnabled && !isUserIdle) {
          console.log('[PACS] Received WebSocket message:', data);
          // Only refresh if not currently loading
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            filterResults(filters, pagination.current, pagination.pageSize);
          }
        }
      } catch (e) {
        console.error('[PACS] Error in WS handler:', e);
      }
    });

    return () => unsubscribe();
  }, [wsContext, autoRefreshEnabled, isUserIdle, orders.loading, filters, pagination]);



  useEffect(() => {
    const IDLE_TIME = 60 * 60 * 1000; // 60 minutes of inactivity

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isUserIdle) {
        setIsUserIdle(false);
      }
    };

    // Throttle the idle timer reset to prevent excessive function calls
    // Only update every 1 second instead of every mouse movement
    const throttledResetIdleTimer = throttle(resetIdleTimer, 1000, {
      leading: true,
      trailing: false,
    });

    const checkIdleStatus = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      if (timeSinceActivity >= IDLE_TIME && !isUserIdle) {
        setIsUserIdle(true);
        console.log('User is idle, pausing auto-refresh');
      } else if (timeSinceActivity < IDLE_TIME && isUserIdle) {
        setIsUserIdle(false);
        console.log('User is active, resuming auto-refresh');
      }
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, throttledResetIdleTimer, true);
    });

    // Check idle status every 30 seconds
    const idleCheckInterval = setInterval(checkIdleStatus, 30 * 1000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetIdleTimer, true);
      });
      clearInterval(idleCheckInterval);
    };
  }, [isUserIdle]); // Removed lastActivity from dependencies!

  useEffect(() => {
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  const debouncedFilter = useCallback(
    debounce(tempFilters => {
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const getUsersList = () => {
    makePostCall('/user-list', {})
      .then(res => {
        setUserList(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setUserList([]);
      });
  };

  const onSave = (newContent, status, currentReport, moreInfo, callback) => {
    const { pacs_order } = reportEditorModal?.data;
    const { patient } = pacs_order;
    const { proxy_user, correlated, diagnosed, co_signing_doctor } = moreInfo;

    makePostCall('/submit-report', {
      html: newContent,
      yh_no: patient.pat_pin,
      order_no: pacs_order?.po_his_ord_no,
      acc_no: pacs_order?.po_acc_no,
      order_id: pacs_order?.pacs_ord_id,
      user_id: getUserDetails()?.username,
      ...moreInfo,
      proxy_user: proxy_user,
      co_signing_doctor: co_signing_doctor,
      status,
      report_id: currentReport?.pr_id,
      correlated: correlated,
      diagnosed: diagnosed,
    })
      .then(res => {
        callback && callback();
        setReportEditorModal({ visible: false, data: null });
      })
      .catch(e => {
        console.log(e);
      });
  };

  const getPacsList = async () => {
    makePostCall('/pacs-list', {
      role: userDetails?.user_type,
    })
      .then(res => {
        setOrders({ data: res.data?.data || [], loading: false });
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false });
      });
  };

  const cancelReport = () => {
    setReportEditorModal({ visible: false, data: {} });
  };

  const openViewer = async (record: any): Promise<void> => {
    if (hasStudyViewingPermission(userDetails)) {
      const viewerWindow = await launchViewerOnDualMonitor(record?.ps_study_uid, record);
      if (!viewerWindow) message.warning('Could not open viewer window');
    } else {
      message.info('You do not have permission to view study images');
    }
  };

  const openRadDesk = record => {
    if (hasStudyViewingPermission(userDetails)) {
      const { pacs_order } = record;
      const { patient, po_site } = pacs_order;
      const { pat_pin } = patient;
      window.open(`${RADIOLOGY_URL(pat_pin, po_site || '')}`, '_blank');
    } else {
      message.info('You do not habe the permission to view the study images');
    }
  };

  const openReport = async record => {
    // In DUAL_VIEWER_MODE, clicking the patient name opens viewers (not the report editor)
    if ((window as any).ENV_CONFIG?.DUAL_VIEWER_MODE) {
      return openViewer(record);
    }
    if (hasReportingPermission(userDetails)) {
      const data = {
        order_ids: [record?.pacs_order?.pacs_ord_id].join(','),
      };
      // Convert data to URL parameters
      const params = new URLSearchParams(data).toString();
      // Open in new tab
      window.open(`/report?${params}`, '_blank');
    } else {
      message.info('You do not have the permission to do reporting');
    }
  };

  const openSelectedStudies = () => {
    if (hasReportingPermission(userDetails)) {
      const data = {
        order_ids: selectedRowKeys.join(','),
      };
      // Convert data to URL parameters
      const params = new URLSearchParams(data).toString();
      // Open in new tab
      window.open(`/report?${params}`, '_blank');
    } else {
      message.info('You do not have the permission to do reporting');
    }
  };

  const getSavedFilters = async () => {
    makePostCall('/get-saved-filters', { user_id: getUserDetails()?.username })
      .then(res => {
        setSavedFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setSavedFilters([]);
      });
  };

  const getQuickFilters = async () => {
    makePostCall('/get-quick-filters')
      .then(res => {
        setQuickFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setQuickFilters([]);
      });
  };

  const submitSaveFilters = () => {
    setSaveFiltersModal({ saveLoading: true });
    const payload = {
      filters: JSON.stringify(filters),
      uf_filter_name: filterName,
      user_id: getUserDetails()?.username,
    };
    makePostCall('/save-my-filters', payload)
      .then(res => {
        getSavedFilters();
        setSaveFiltersModal({ visible: false });
      })
      .catch(e => {
        console.log(e);
        message.error(e);
      });
  };

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    setSelectedRowKeys([]);
    // Don't call filterResults directly - let the useEffect on line 112-119 handle it via debouncedFilter
  };

  const filterResults = (tempFilters, page = 1, pageSize = 20) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize,
    };

    if (dateRange) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = dateRange;
    }

    makePostCall('/pacs-list', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 });
      });
  };

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  };

  const statusOptions =
    userDetails?.user_type === 'doc'
      ? [{ label: 'SIGNEDOFF', value: 'SIGNEDOFF' }]
      : [
          // { label: 'PENDING', value: 'PENDING' },
          { label: 'SCANNED', value: 'SCANNED' },
          { label: 'DRAFTED', value: 'DRAFTED' },
          { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
        ];

  const handleFilterSelection = selectedSavedFilter => {
    const filterString = selectedSavedFilter.uf_filter_json;
    const filterJson = JSON.parse(filterString);

    setFilters(filterJson);
  };

  const handleQuickFilters = selectedFilter => {
    const { qf_type, qf_date_start, qf_date_end, qf_key, qf_value } = selectedFilter;
    if (qf_type === 'DATE') {
      // study_date
      const toDate = dayjs().subtract(qf_date_end, 'days');
      const fromDate = dayjs().subtract(qf_date_start, 'days');
      setDateRange([fromDate, toDate]);
      // setFilters({})
    } else if (qf_type === 'TOGGLE') {
      // Toggle the filter value (for Emergency, Re-reported, etc.)
      handleFilterChange(qf_key, !filters[qf_key]);
    } else {
      setFilters({
        ...filters,
        [qf_key]: qf_value,
      });
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const showOrdDetails = rec => {
    setOrderInfoModal({ visible: true, data: rec });
  };

  const closeOrdInfo = () => {
    setOrderInfoModal({ visible: false, data: null });
  };

  const assignToUser = () => {
    setAssignModal({ visible: false, data: {} });
    // send request to assign users to the selected report
    makePostCall('/assign-to-user', {
      order_ids: selectedRowKeys,
      assigned_to: selectedUsersToAssign,
    })
      .then(res => {
        // refresh the report data with current pagination
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log(e);
      });
  };

  const addFile = rec => {
    setAddFileModal({ visible: true, data: rec, modalType: 'upload' });
  };

  const uploadRisNotes = async () => {
    const { data, modalType, fileType, notes, file, isNotes } = addFileModal;

    const { pacs_order } = data;
    const formData = new FormData();

    if (!isNotes && file?.size / 1024 > 500) {
      message.error('File size should be less than 500kb', 5);
      return;
    }

    formData.append('order_ids', pacs_order?.pacs_ord_id);
    formData.append('order_id', pacs_order?.pacs_ord_id);
    formData.append('pin', pacs_order?.patient?.pat_pin);

    let headers = {
      'Content-Type': 'multipart/form-data',
    };
    if (isNotes) {
      formData.append('type', 'notes');
      formData.append('notes', notes);
    } else {
      formData.append('file_type', fileType);
      formData.append('file', file);
    }

    const url = isNotes ? '/upload-notes' : '/upload-file';
    formData.append('user_id', getUserDetails()?.username);

    try {
      const res = await makePostCall(url, formData, { headers });
      notification.success({
        message: 'Success',
        description: 'File uploaded successfully',
      });
      setAddFileModal(null);
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      console.log(msg);
      notification.error({
        message: 'Error',
        description: `Error while uploading file [${msg}]`,
      });
    }
  }


  const captureAndPrint = () => {
    const receiverInfo = receiverForm.getFieldsValue();
    printReport(receiverModal?.details, receiverInfo);
  };

  const printReport = rec => {
    setPrintLoading(true);
    const { pacs_order } = rec;
    const { patient } = pacs_order;

    makePostCall(
      '/print-acc-report',
      {
        order_id: pacs_order.pacs_ord_id,
        user_id: getUserDetails()?.username,
      },
      {
        responseType: 'arraybuffer',
      }
    )
      .then(res => {
        // Create filename from patient details
        const sanitize = str => (str ? str.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_') : '');
        const patientName = sanitize(patient?.pat_name || 'Unknown');
        const studyDesc = sanitize(pacs_order?.po_diag_desc || 'Study');
        const studyDate = pacs_order?.po_study_date
          ? dayjs(pacs_order.po_study_date).format('YYYYMMDD')
          : '';
        const filename = `${patientName}_${studyDesc}_${studyDate}.pdf`;

        const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);

        // Open PDF in new window with download button
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${filename.replace('.pdf', '')}</title>
              <style>
                body {
                  margin: 0;
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  height: 100vh;
                }
                .controls {
                  padding: 10px;
                  background: #f0f0f0;
                  border-bottom: 1px solid #ccc;
                  text-align: center;
                }
                .controls button {
                  margin: 0 5px;
                  padding: 8px 16px;
                  cursor: pointer;
                  background: #007bff;
                  color: white;
                  border: none;
                  border-radius: 4px;
                  font-size: 14px;
                }
                .controls button:hover {
                  background: #0056b3;
                }
                embed {
                  flex: 1;
                  width: 100%;
                  border: none;
                }
              </style>
            </head>
            <body>
              <div class="controls">
                <button onclick="downloadPDF()">Download PDF</button>
              </div>
              <embed src="${url}" type="application/pdf" />
              <script>
                function downloadPDF() {
                  const a = document.createElement('a');
                  a.href = '${url}';
                  a.download = '${filename}';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              </script>
            </body>
            </html>
          `);
          printWindow.document.close();
        }

        // Clean up after delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 30000);
      })
      .catch(err => {
        console.log('Error', err);
      })
      .finally(() => {
        setPrintLoading(false);
      });
  };

  const downloadDocxReport = rec => {
    setPrintLoading(true);
    const { pacs_order } = rec;

    makeGetCall(`/download-pat-report-docx?order_id=${pacs_order.pacs_ord_id}`, {
      responseType: 'arraybuffer',
    })
      .then(res => {
        // Extract filename from Content-Disposition header if available
        let filename = `report-${pacs_order.pacs_ord_id}-${pacs_order?.patient?.pat_name}.docx`;
        // Create blob and download
        const docxBlob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const url = URL.createObjectURL(docxBlob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);

        message.success('DOCX report downloaded successfully');
      })
      .catch(err => {
        console.log('Error downloading DOCX report', err);
        message.error('Failed to download DOCX report');
      })
      .finally(() => {
        setPrintLoading(false);
      });
  };

  const viewReport = rec => {
    setPrintLoading(true);
    const { pacs_order } = rec;
    const { pacs_ord_id, patient } = pacs_order;
    const { pat_pacs_id } = patient;
    makePostCall(
      '/print-acc-report',
      {
        order_id: pacs_order.pacs_ord_id,
        user_id: getUserDetails()?.username,
      },
      {
        responseType: 'arraybuffer',
      }
    )
      .then(res => {
        setViewerModal({
          visible: true,
          data: res.data,
          patient_id: pat_pacs_id,
          order_id: pacs_ord_id,
          patientData: rec, // Pass the full record for WhatsApp sharing
        });
      })
      .catch(err => {
        console.log('Error', err);
        setViewerModal({ visible: false, data: null });
      })
      .finally(() => {
        setPrintLoading(false);
      });
  };

  const refetchPdf = () => {
    if (!viewerModal?.order_id) return;

    setPdfRefreshing(true);
    makePostCall(
      '/print-acc-report',
      {
        order_id: viewerModal.order_id,
        user_id: getUserDetails()?.username,
      },
      {
        responseType: 'arraybuffer',
      }
    )
      .then(res => {
        setViewerModal(prev => ({
          ...prev,
          data: res.data,
        }));
        message.success('PDF refreshed successfully');
      })
      .catch(err => {
        console.log('Error refreshing PDF', err);
        message.error('Failed to refresh PDF');
      })
      .finally(() => {
        setPdfRefreshing(false);
      });
  };

  const editSignedOffReport = rec => {
    const { pacs_order } = rec;
    const { pacs_ord_id, patient } = pacs_order;
    const { pat_pacs_id } = patient;
    console.log('Editing report for order ID for pacs list :', pacs_ord_id);
    makePostCall('/get-reports', {
      order_id: pacs_ord_id,
    })
      .then(res => {
        const reports = res.data?.data || [];
        const signedOffReport = reports.find(r => r.pr_status === 'SIGNEDOFF') || reports[0];

        if (signedOffReport) {
          setEditReportModal({
            visible: true,
            reportData: signedOffReport,
            patient_id: pat_pacs_id,
            order_id: pacs_ord_id,
            patientData: rec,
          });
        } else {
          message.error('No signed-off report found');
        }
      })
      .catch(err => {
        console.log('Error', err);
        message.error('Failed to load report for editing');
      });
  };

  const viewNotes = rec => {
    const firstNote = rec.note_id || {};
    setSelectedNote(firstNote);
    setViewNotesModal({ visible: true, details: rec });
  };

  const handleNoteSelection = (note, record) => {
    setSelectedNote(note);
  };

  const toggleReporting = (rec, idx) => {
    const { pacs_order } = rec;
    const { po_pacs_ord_id } = pacs_order;
    const shouldBlock = po_block_reporting !== 'Y';
    // toggle-reporting
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: po_pacs_ord_id,
      shouldBlock,
    };
    makePostCall('/toggle-reporting', payload)
      .then(res => {
        message.success('Updated successfully');
        // Update by ID instead of index to support pagination
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );
        if (recordIndex !== -1) {
          prevOrders[recordIndex].po_block_reporting = shouldBlock ? 'Y' : 'N';
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  const toggleFeatures = (rec, idx, feature) => {
    const { pacs_order, order_workflow } = rec;
    const { pacs_ord_id, po_priority, po_his_status } = pacs_order;
    const { ow_reporting_blocked } = order_workflow;

    const shouldConfirm = po_his_status !== 'CONFIRMED';
    const currentlyBlocked = ow_reporting_blocked === true || ow_reporting_blocked === 'Y';
    const shouldBlock = !currentlyBlocked;
    const markEmergency = po_priority !== 'EMERGENCY';

    // toggle-reporting
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: pacs_ord_id,
      // shouldConfirm,
    };

    let url = '';

    switch (feature) {
      case 'emergency':
        payload.markEmergency = markEmergency;
        url = '/toggle-emergency';
        objProperty = 'po_priority';
        objValue = markEmergency ? 'EMERGENCY' : 'NORMAL';
        break;
      case 'confirmation':
        payload.shouldConfirm = shouldConfirm;
        url = '/toggle-confirmation';
        objProperty = 'po_his_status';
        objValue = shouldConfirm ? 'CONFIRMED' : 'UNCONFIRMED';
        break;
      case 'reporting':
        payload.shouldBlock = shouldBlock;
        url = '/toggle-reporting';
        break;
      default:
        return;
    }

    makePostCall(url, payload)
      .then(res => {
        message.success('Updated successfully');
        // Refresh current page to reflect changes
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  const toggleConfirmation = (rec, idx) => {
    const { pacs_order } = rec;
    const { pacs_ord_id, po_his_status } = pacs_order;
    const shouldConfirm = po_his_status !== 'CONFIRMED';
    // toggle-reporting
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: pacs_ord_id,
      shouldConfirm,
    };
    makePostCall('/toggle-confirmation', payload)
      .then(res => {
        message.success('Updated successfully');
        // Update by ID instead of index to support pagination
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );
        if (recordIndex !== -1) {
          prevOrders[recordIndex].po_his_status = shouldConfirm ? 'CONFIRMED' : 'UNCONFIRMED';
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  const assignToSelfTechnician = (rec, idx) => {
    // assign-to-tech
    const { pacs_order } = rec;
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: pacs_order?.pacs_ord_id,
    };
    makePostCall('/assign-to-tech', payload)
      .then(res => {
        message.success('Updated successfully');
        // Update by ID instead of index to support pagination
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );
        if (recordIndex !== -1) {
          prevOrders[recordIndex].order_workflow.ow_assigned_technician =
            getUserDetails()?.username;
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  const handleRadiologistAssignment = (record, radiologistId, idx) => {
    const { pacs_order } = record;

    makePostCall('/assign-to-user', {
      order_ids: [pacs_order?.pacs_ord_id],
      assigned_to: radiologistId || null,
    })
      .then(res => {
        message.success(radiologistId ? 'Assigned successfully' : 'Unassigned successfully');

        // Update local state to reflect change immediately
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );

        if (recordIndex !== -1) {
          prevOrders[recordIndex].order_workflow.ow_assigned_rad_id = radiologistId || null;
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log(e);
        message.error('Failed to update assignment');
      });
  };

  const reportedByOptions = useMemo(() => {
    return userList?.map(user => ({ label: user.user_fullname, value: user.username }));
  }, [userList]);

  const closeReport = async data => {
    setReportEditorModal({ visible: false });
    const resp = await makePostCall('/close-report', { order_id: data?.pacs_order?.pacs_ord_id });
  };

  const FIXED_COLUMNS_COUNT = 2; // Pat ID and Patient Name are non-draggable

  const saveColumnPreferences = async (newColumnOrder, newPinnedColumns?: PinnedColumnsConfig) => {
    try {
      // Create a simplified version of column order (just store the keys/identifiers)
      // Pinning info is embedded per-column in the same JSON
      const columnKeys = newColumnOrder.map(col => {
        const colId = col.key || col.dataIndex || '';
        const identifier: any = {
          key: col.key || null,
          dataIndex: col.dataIndex || null,
          title: typeof col.title === 'string' ? col.title : null,
        };

        // If title is a React element, try to extract text
        if (typeof col.title !== 'string' && col.title?.props?.children) {
          if (typeof col.title.props.children === 'string') {
            identifier.title = col.title.props.children;
          } else if (Array.isArray(col.title.props.children)) {
            // Extract text from children array (skip icons)
            const textContent = col.title.props.children
              .filter(child => typeof child === 'string')
              .join('');
            identifier.title = textContent;
          }
        }

        // Embed pinning info per-column
        if (newPinnedColumns) {
          if (newPinnedColumns.left.includes(colId)) {
            identifier.pinned = 'left';
          } else if (newPinnedColumns.right.includes(colId)) {
            identifier.pinned = 'right';
          }
        }

        return identifier;
      });

      await makePostCall('/save-column-preferences', {
        user_id: getUserDetails()?.username,
        module: 'pacs_list',
        column_order: JSON.stringify(columnKeys),
      });

      console.log('Column preferences saved successfully');
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      setIsLoadingColumnPreferences(true);
      const response = await makeGetCall(
        `/get-column-preferences?user_id=${getUserDetails()?.username}&module=pacs-list`
      );

      // Get the base columns
      const baseColumns = orderColumns({
        openViewer: openViewer,
        openRadDesk,
        openReportEditor: openReport,
        role: userDetails?.user_type,
        assignToSelfTechnician,
        toggleReporting,
        addFile,
        viewNotes,
        printReport,
        downloadDocxReport,
        viewReport,
        editSignedOffReport,
        toggleConfirmation,
        toggleFeatures,
        showOrdDetails,
        handleShareStudy,
        handleDownloadStudy,
        editStudyDetails: handleEditStudyDetails,
        showStudyHistory: handleShowStudyHistory,
        handleRadiologistAssignment,
        userList,
        openRadiantViewer,
        enableRadiantViewer: true,
      });

      if (response.data?.data?.ucp_column_order) {
        const savedColumnKeys = JSON.parse(response.data.data.ucp_column_order);

        // Extract pinning info from per-column data
        const loadedPinned: PinnedColumnsConfig = { left: [], right: [] };
        savedColumnKeys.forEach(savedCol => {
          const colId = savedCol.key || savedCol.dataIndex;
          if (colId && savedCol.pinned === 'left') {
            loadedPinned.left.push(colId);
          } else if (colId && savedCol.pinned === 'right') {
            loadedPinned.right.push(colId);
          }
        });
        setPinnedColumns(loadedPinned);

        // Create a simple reordering approach
        const reorderedColumns = [];

        // First, add fixed columns in their original order
        for (let i = 0; i < FIXED_COLUMNS_COUNT && i < baseColumns.length; i++) {
          reorderedColumns.push(baseColumns[i]);
        }

        // For non-fixed columns, try to match saved order
        const nonFixedBaseColumns = baseColumns.slice(FIXED_COLUMNS_COUNT);
        const nonFixedSavedKeys = savedColumnKeys.slice(FIXED_COLUMNS_COUNT);

        // Try to reorder based on saved preferences
        nonFixedSavedKeys.forEach(savedCol => {
          const matchingColumn = nonFixedBaseColumns.find(col => {
            // Try multiple matching strategies
            return (
              (col.key && savedCol.key && col.key === savedCol.key) ||
              (col.dataIndex && savedCol.dataIndex && col.dataIndex === savedCol.dataIndex) ||
              (typeof col.title === 'string' &&
                typeof savedCol.title === 'string' &&
                col.title === savedCol.title)
            );
          });

          if (matchingColumn) {
            reorderedColumns.push(matchingColumn);
          }
        });

        // Add any columns that weren't in saved preferences (new columns)
        nonFixedBaseColumns.forEach(col => {
          const isAlreadyAdded = reorderedColumns.some(addedCol => {
            return (
              (col.key && addedCol.key && col.key === addedCol.key) ||
              (col.dataIndex && addedCol.dataIndex && col.dataIndex === addedCol.dataIndex) ||
              (typeof col.title === 'string' &&
                typeof addedCol.title === 'string' &&
                col.title === addedCol.title)
            );
          });

          if (!isAlreadyAdded) {
            reorderedColumns.push(col);
          }
        });

        setColumnOrder(reorderedColumns);
      } else {
        // No saved preferences OR new column structure detected, use default order
        // Default: pin actions_column to right for first-time users
        setPinnedColumns({ left: [], right: ['actions_column'] });
        setColumnOrder([]);
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
      // If loading fails, use defaults
      setPinnedColumns({ left: [], right: ['actions_column'] });
      setColumnOrder([]);
    } finally {
      setIsLoadingColumnPreferences(false);
    }
  };

  // Debounced save function to avoid too many API calls
  const debouncedSaveColumnPreferences = useCallback(
    debounce((columnOrder, currentPinnedColumns?: PinnedColumnsConfig) => {
      if (columnOrder.length > 0) {
        saveColumnPreferences(columnOrder, currentPinnedColumns);
      }
    }, 1000),
    []
  );
  // Add these drag and drop handler functions with useCallback
  const handleDragStart = useCallback((e, index) => {
    // Only allow dragging if the column is not in the fixed range
    if (index < FIXED_COLUMNS_COUNT) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();

      // Don't allow dropping in the fixed columns area
      if (dropIndex < FIXED_COLUMNS_COUNT) {
        return;
      }

      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));

      // Don't allow dragging fixed columns
      if (dragIndex < FIXED_COLUMNS_COUNT || dragIndex === dropIndex) {
        return;
      }

      // Use the current columnOrder state if it exists, otherwise get base columns
      const currentColumns =
        columnOrder.length > 0
          ? columnOrder
          : orderColumns({
              openViewer: openViewer,
              openRadDesk,
              openReportEditor: openReport,
              role: userDetails?.user_type,
              assignToSelfTechnician,
              openRadiantViewer,
              enableRadiantViewer,
              toggleReporting,
              addFile,
              viewNotes,
              printReport,
              downloadDocxReport,
              viewReport,
              editSignedOffReport,
              toggleConfirmation,
              toggleFeatures,
              showOrdDetails,
              handleShareStudy,
              handleDownloadStudy,
              editStudyDetails: handleEditStudyDetails,
              showStudyHistory: handleShowStudyHistory,
              handleRadiologistAssignment,
              userList,
            });

      const newColumns = [...currentColumns];
      const draggedColumn = newColumns[dragIndex];

      // Remove the dragged column
      newColumns.splice(dragIndex, 1);
      // Insert it at the new position
      newColumns.splice(dropIndex, 0, draggedColumn);

      setColumnOrder(newColumns);

      // Save the new column order to backend (debounced)
      debouncedSaveColumnPreferences(newColumns, pinnedColumns);
    },
    [columnOrder, pinnedColumns, userDetails?.user_type, debouncedSaveColumnPreferences]
  );

  // Header actions for PacsAgGrid columns (gear icon in Actions column header)
  const columnHeaderActions: ColumnHeaderActions = useMemo(() => ({
    actions_column: {
      icon: <SettingOutlined />,
      onClick: () => setColumnConfiguratorVisible(true),
      tooltip: 'Configure Columns',
    },
  }), []);

  // Create a memoized function to generate draggable columns
  const getDraggableColumns = useMemo(() => {
    const baseColumns =
      columnOrder.length > 0
        ? columnOrder
        : orderColumns({
            openViewer: openViewer,
            openRadDesk,
            openReportEditor: openReport,
            role: userDetails?.user_type,
            assignToSelfTechnician,
            toggleReporting,
            addFile,
            viewNotes,
            printReport,
            downloadDocxReport,
            viewReport,
            editSignedOffReport,
            toggleConfirmation,
            toggleFeatures,
            showOrdDetails,
            handleShareStudy,
            handleDownloadStudy,
            editStudyDetails: handleEditStudyDetails,
            showStudyHistory: handleShowStudyHistory,
            handleRadiologistAssignment,
            userList,
            openRadiantViewer,
            enableRadiantViewer,
          });

    return baseColumns.map((col, index) => {
      const colId = col.key || col.dataIndex;
      // Single source of truth: pinnedColumns state (driven by DB)
      let fixed: string | undefined;
      if (pinnedColumns.left.includes(colId)) {
        fixed = 'left';
      } else if (pinnedColumns.right.includes(colId)) {
        fixed = 'right';
      }

      return {
        ...col,
        fixed,
        title:
          index < FIXED_COLUMNS_COUNT ? (
            // Fixed columns - no drag handle, just the title
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                userSelect: 'none',
              }}
            >
              {col.title}
            </div>
          ) : (
            // Draggable columns - without visible drag handle
            <div
              className="draggable-header"
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'move',
                userSelect: 'none',
              }}
            >
              {col.title}
            </div>
          ),
      };
    });
  }, [
    columnOrder,
    pinnedColumns,
    userDetails?.user_type,
    userList,
    handleRadiologistAssignment,
    openRadiantViewer,
    enableRadiantViewer,
  ]);

  // Get column configs for the configurator modal
  const allColumnConfigs: ColumnConfig[] = useMemo(() => {
    const baseColumns = orderColumns({
      openViewer: openViewer,
      openRadDesk,
      openReportEditor: openReport,
      role: userDetails?.user_type,
      assignToSelfTechnician,
      toggleReporting,
      addFile,
      viewNotes,
      printReport,
      downloadDocxReport,
      viewReport,
      editSignedOffReport,
      toggleConfirmation,
      toggleFeatures,
      showOrdDetails,
      handleShareStudy,
      handleDownloadStudy,
      editStudyDetails: handleEditStudyDetails,
      showStudyHistory: handleShowStudyHistory,
      handleRadiologistAssignment,
      userList,
      openRadiantViewer,
      enableRadiantViewer,
    });

    return baseColumns.map(col => {
      // Extract header name from title (handles React elements)
      let headerName = '';
      if (typeof col.title === 'string') {
        headerName = col.title;
      } else if (col.title?.props?.children) {
        const children = col.title.props.children;
        if (typeof children === 'string') {
          headerName = children;
        } else if (Array.isArray(children)) {
          headerName = children.filter(c => typeof c === 'string').join('');
        }
      }

      return {
        colId: col.key || col.dataIndex || '',
        headerName: headerName || col.key || col.dataIndex || '',
      };
    });
  }, [userDetails?.user_type, userList]);

  // Handle column configuration save from the configurator modal
  const handleColumnConfigSave = useCallback((newVisibleColumnIds: string[], newPinnedColumns: PinnedColumnsConfig) => {
    setVisibleColumnIds(newVisibleColumnIds);
    setPinnedColumns(newPinnedColumns);

    // Rebuild column order based on visible column IDs
    const baseColumns = orderColumns({
      openViewer: openViewer,
      openRadDesk,
      openReportEditor: openReport,
      role: userDetails?.user_type,
      assignToSelfTechnician,
      toggleReporting,
      addFile,
      viewNotes,
      printReport,
      downloadDocxReport,
      viewReport,
      editSignedOffReport,
      toggleConfirmation,
      toggleFeatures,
      showOrdDetails,
      handleShareStudy,
      handleDownloadStudy,
      editStudyDetails: handleEditStudyDetails,
      showStudyHistory: handleShowStudyHistory,
      handleRadiologistAssignment,
      userList,
      openRadiantViewer,
      enableRadiantViewer,
    });

    // Reorder columns based on visible column IDs
    const reorderedColumns: any[] = [];
    newVisibleColumnIds.forEach(colId => {
      const col = baseColumns.find(c => (c.key || c.dataIndex) === colId);
      if (col) {
        reorderedColumns.push(col);
      }
    });

    setColumnOrder(reorderedColumns);
    saveColumnPreferences(reorderedColumns, newPinnedColumns);
  }, [userDetails?.user_type, userList, openRadiantViewer, enableRadiantViewer]);

  return (
    <Spin spinning={printLoading}>
      <div>
        {/* Tab bar with action buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>{StyledTabsComponent && StyledTabsComponent()}</div>
          <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.25rem' }}>
            <button
              className="btn-secondary"
              onClick={() => {
                clearFilters();
              }}
            >
              Clear Filters
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setSaveFiltersModal({ visible: true });
              }}
            >
              Save Filters
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setPagination({ current: 1, pageSize: pagination.pageSize });
                filterResults(filters, 1, pagination.pageSize);
              }}
            >
              Search
            </button>
            {!isHOD && (
              <button
                className="btn-primary"
                disabled={!selectedRowKeys.length}
                onClick={() => openSelectedStudies()}
              >
                Open Studies
              </button>
            )}
            {isHOD && (
              <button
                className="btn-primary"
                disabled={!selectedRowKeys.length}
                onClick={() => {
                  setAssignModal({ visible: true });
                }}
              >
                Assign
              </button>
            )}
          </div>
        </div>
        <div style={{ padding: '0 1.25rem' }}>
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '0.75rem',
              border: '1px solid #E2E8F1',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
            }}
          >
            <SavedSearches
              savedFilters={savedFilters || []}
              handleFilterSelection={handleFilterSelection}
              quickFilters={quickFilters || []}
              handleQuickFilters={handleQuickFilters}
              currentFilters={filters}
            />
            <div
              className="!"
              style={{ marginTop: '1rem' }}
            >
              {/* <Space.Compact> */}
              {/* <span style={{ width: 140 }} className='!ms-3'>Patient Name</span> */}
              <div
                className="pacs-filters-grid"
                style={
                  {
                    '--primary-color': 'var(--primary-color, #7D48B0)',
                  } as React.CSSProperties
                }
              >
                {/* Row 1: Patient Name | Pat ID | Acc. No | Ref Doc */}
                <FloatLabel
                  label={FieldLabels.PATIENT_NAME}
                  value={filters['pat_name']}
                >
                  <FyzksInput
                    width={400}
                    value={filters['pat_name'] || ''}
                    onChange={e => handleFilterChange('pat_name', e.target.value)}
                  />
                </FloatLabel>

                <FloatLabel
                  label={FieldLabels.PATIENT_ID}
                  value={filters['po_pin']}
                >
                  <FyzksInput
                    value={filters['po_pin'] || ''}
                    width={200}
                    onChange={e => handleFilterChange('po_pin', e.target.value)}
                  />
                </FloatLabel>

                <FloatLabel
                  label={FieldLabels.ACCESSION_NUMBER_SHORTEST}
                  value={filters['po_acc_no']}
                >
                  <FyzksInput
                    value={filters['po_acc_no'] || ''}
                    width={200}
                    onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                  />
                </FloatLabel>

                <FloatLabel
                  label={FieldLabels.REFERRING_DOCTOR}
                  value={filters['po_ref_doc']}
                >
                  <FyzksInput
                    value={filters['po_ref_doc'] || ''}
                    width={200}
                    onChange={e => handleFilterChange('po_ref_doc', e.target.value)}
                  />
                </FloatLabel>

                {/* Row 2: Study Date | Body Part | Status | Modality */}
                <FloatLabel
                  label={FieldLabels.STUDY_DATE}
                  value={filters['study_date']}
                >
                  <RangePicker
                    style={{ width: '25rem' }}
                    size="middle"
                    value={dateRange}
                    onChange={val => {
                      if (val) {
                        setDateRange([val[0], val[1]]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </FloatLabel>

                <FloatLabel
                  label="Body Part / Study Desc"
                  value={filters['po_body_part']}
                >
                  <FyzksInput
                    value={filters['po_body_part'] || ''}
                    width={200}
                    onChange={e => handleFilterChange('po_body_part', e.target.value)}
                  />
                </FloatLabel>

                <FloatLabel
                  label="Status"
                  value={filters['pacs_status']}
                >
                  <Select
                    value={filters['pacs_status']}
                    allowClear
                    style={{ width: '12.5rem' }}
                    options={statusOptions}
                    onChange={val => handleFilterChange('pacs_status', val)}
                  />
                </FloatLabel>

                <FloatLabel
                  label={FieldLabels.MODALITY}
                  value={filters['modality']}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    value={filters['modality']}
                    style={{ width: '12.5rem' }}
                    options={modalityOptions}
                    onChange={val => handleFilterChange('modality', val)}
                  />
                </FloatLabel>

                {/* Commented out filters for future use */}
                {/* <FloatLabel label="Visit No" value={filters['po_visit_number']}>
                <FyzksInput value={filters['po_visit_number']} width={200} onChange={(e) => handleFilterChange('po_visit_number', e.target.value)} />
              </FloatLabel>
              <FloatLabel label="HIS Status" value={filters['po_his_status']}>
                <Select value={filters['po_his_status']} allowClear style={{ width: 200 }} options={hisStatusOptions} onChange={(val) => handleFilterChange('po_his_status', val)} />
              </FloatLabel>
              <FloatLabel label="Order No" value={filters['his_ord_no']}>
                <FyzksInput value={filters['his_ord_no']} width={200} onChange={(e) => handleFilterChange('his_ord_no', e.target.value)} />
              </FloatLabel>
              <FloatLabel label="Site" value={filters['site']}>
                <Select value={filters['site']} allowClear style={{ width: 200 }} options={siteOptions} onChange={(val) => handleFilterChange('site', val)} />
              </FloatLabel>
              <FloatLabel label="Reg. Type" value={filters['po_pat_class']}>
                <Select allowClear value={filters['po_pat_class']} style={{ width: 200 }} options={regTypeOptions} onChange={(val) => handleFilterChange('po_pat_class', val)} />
              </FloatLabel>
              <FloatLabel label="Reported By" value={filters['po_reported_by']}>
                <Select showSearch value={filters['po_reported_by']} style={{ width: 200 }} filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={reportedByOptions} onChange={(val) => handleFilterChange('po_reported_by', val)} />
              </FloatLabel>
              <FloatLabel label="Assigned to" value={filters['assigned_to']}>
                <Select showSearch value={filters['assigned_to']} style={{ width: 200 }} filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={reportedByOptions} onChange={(val) => handleFilterChange('assigned_to', val)} />
              </FloatLabel> */}
              </div>
            </div>
            {/* QuickFilters component removed - now integrated into SavedSearches component above */}
          </div>
          <div className="orders-list">
            <PacsAgGrid
              height="calc(100vh - 24rem)"
              rowData={orders.data || []}
              loading={orders.loading}
              columns={getDraggableColumns}
              fixedColumnsCount={FIXED_COLUMNS_COUNT}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={(keys) => setSelectedRowKeys(keys)}
              onRowClick={openViewer}
              onOpenReport={openReport}
              onOpenRadiant={openRadiantViewer}
              enableRadiantViewer={enableRadiantViewer}
              columnHeaderActions={columnHeaderActions}
              userList={userList}
              onRadiologistAssignment={handleRadiologistAssignment}
              onAssignToSelfTechnician={assignToSelfTechnician}
              onColumnOrderChange={(newCols) => {
                // Save column order to backend (include current pinning)
                debouncedSaveColumnPreferences(newCols, pinnedColumns);
              }}
            />
            {/* External pagination for server-side paging */}
            <div className="pacs-ag-grid-pagination">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={orders?.total || 0}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={(page, pageSize) => {
                  setPagination({ current: page, pageSize });
                  setSelectedRowKeys([]);
                  filterResults(filters, page, pageSize);
                }}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
              />
            </div>

            {/* Column Configurator Modal */}
            <ColumnConfigurator
              visible={columnConfiguratorVisible}
              onClose={() => setColumnConfiguratorVisible(false)}
              allColumns={allColumnConfigs}
              visibleColumnIds={visibleColumnIds}
              pinnedColumns={pinnedColumns}
              onSave={handleColumnConfigSave}
            />
            {reportEditorModal.visible && (
              <Modal
                className="report-modal"
                width={'100%'}
                onCancel={() => {
                  closeReport(reportEditorModal?.data);
                }}
                footer={null}
                open={reportEditorModal.visible}
                style={{ top: 20 }} // Adjust position
                styles={{ body: { height: '90vh', overflowY: 'auto' } }}
                keyboard={false}
              >
                <ReportEditor
                  cancel={cancelReport}
                  onSave={onSave}
                  patientDetails={reportEditorModal.data}
                />
              </Modal>
            )}

            {saveFiltersModal.visible && (
              <Modal
                style={{
                  height: 'auto',
                }}
                styles={{
                  wrapper: {
                    transformOrigin: 'top center',
                  },
                }}
                className="save-filter-modal"
                onCancel={() => {
                  setSaveFiltersModal({ visible: false });
                }}
                okButtonProps={{ disabled: !filterName || saveFiltersModal.saveLoading }}
                onOk={submitSaveFilters}
                open={saveFiltersModal.visible}
              >
                <div className="pt-4">
                  <label>Enter a name for this filter set:</label>
                  <Input
                    placeholder="Type the name for the filters"
                    style={{ width: '18.75rem' }}
                    onChange={e => setFilterName(e.target.value)}
                  />
                </div>
              </Modal>
            )}

            {assignModal.visible && (
              <Modal
                className="save-filter-modal"
                onCancel={() => {
                  setAssignModal({ visible: false });
                }}
                okButtonProps={{ disabled: !selectedUsersToAssign }}
                onOk={assignToUser}
                open={assignModal.visible}
              >
                Select User
                <div>
                  <Select
                    showSearch
                    style={{ width: '12.5rem' }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={userList?.map(user => ({
                      label: user.user_fullname,
                      value: user.username,
                    }))}
                    onChange={val => {
                      setSelectedUsersToAssign(val);
                    }}
                  />
                </div>
              </Modal>
            )}

            {addFileModal && addFileModal?.visible && (
              <Modal
                open={addFileModal?.visible}
                onOk={() => {
                  uploadRisNotes();
                }}
                onCancel={() => {
                  setAddFileModal({});
                }}
                okButtonProps={{
                  disabled:
                    (addFileModal?.modalType === 'upload' && !addFileModal?.file) ||
                    (addFileModal?.modalType === 'notes' && !addFileModal?.notes),
                }}
              >
                <Tabs
                  activeKey={addFileModal.modalType}
                  onTabClick={activeKey => {
                    if (addFileModal.modalType !== activeKey) {
                      setAddFileModal({
                        ...addFileModal,
                        modalType: activeKey,
                        isNotes: activeKey === 'notes',
                      });
                    }
                  }}
                >
                  <TabPane
                    key="notes"
                    tab="Notes"
                  >
                    <div style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ fontWeight: 500, fontSize: '1rem' }}>
                          Enter Notes / Remarks
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                          Add any additional notes or remarks about this study
                        </p>
                      </div>
                      <TextArea
                        placeholder="Type your notes here..."
                        rows={6}
                        onChange={e => {
                          setAddFileModal({ ...addFileModal, notes: e.target.value });
                        }}
                        style={{
                          fontSize: '0.9rem',
                          borderRadius: '0.5rem',
                          border: '1px solid #d9d9d9',
                        }}
                      />
                    </div>
                  </TabPane>
                  <TabPane
                    key="upload"
                    tab="File Upload"
                  >
                    <div style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label className='file-box-text'>
                          Select File Type
                        </label>
                        <Select
                          placeholder="Choose file type"
                          options={[
                            { label: 'Prescription', value: 'prescription' },
                            { label: 'Consent', value: 'consent' },
                          ]}
                          style={{ width: '100%' }}
                          onChange={val => {
                            setAddFileModal({ ...addFileModal, fileType: val });
                          }}
                        />
                      </div>
                      <div>
                        <label className='file-box-text'>
                          Upload PDF File
                        </label>
                        <div
                          className={`upload-pdf-box ${
                            addFileModal.fileType ? 'active' : ''
                          }`}
                        >
                          <Upload
                            customRequest={({ file }) => {
                              setAddFileModal({ ...addFileModal, file });
                            }}
                            accept="application/pdf"
                            multiple={false}
                            disabled={!addFileModal.fileType}
                          >
                            <Button
                              disabled={!addFileModal?.fileType}
                              className={`upload-pdf-button ${
                                !addFileModal?.fileType ? 'active' : ''
                              }`}
                            >
                              📁 Select File
                            </Button>
                          </Upload>
                          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666' }}>
                            {addFileModal.file ? (
                              <span style={{ color: '#00795C', fontWeight: 500 }}>
                                ✓ {addFileModal.file.name}
                              </span>
                            ) : (
                              <>
                                <span>Max file size: 500 KB</span>
                                <br />
                                <span>Supported: PDF only</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabPane>
                </Tabs>
              </Modal>
            )}

            {viewNotesModal && viewNotesModal.visible && (
              <Modal
                open={viewNotesModal.visible}
                onCancel={() => {
                  setViewNotesModal(null);
                }}
                onOk={() => {
                  setViewNotesModal(null);
                }}
                style={{ width: '100%', height: '100%' }}
                width={'90%'}
                okButtonProps={{
                  style: {
                    marginTop: '20px',
                  },
                }}
              >
                <ViewNotes
                  handleNoteSelection={handleNoteSelection}
                  selectedNote={selectedNote}
                  viewNotesModal={viewNotesModal}
                />
              </Modal>
            )}
            {receiverModal && receiverModal.visible && (
              <Modal
                open={receiverModal.visible}
                onCancel={() => {
                  setReceiverModal(null);
                }}
                onOk={() => {
                  setReceiverModal(null);
                }}
                style={{ width: '100%', height: '100%' }}
                width={'90%'}
                okButtonProps={{ style: { display: 'none' } }}
              >
                <Form
                  form={receiverForm}
                  onFinish={() => {
                    captureAndPrint();
                  }}
                >
                  <Form.Item
                    name="receiver_name"
                    label="Receiver Name"
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="receiver_mobile"
                    label="Receiver Mobile"
                    rules={[
                      {
                        pattern: /^[6-9]\d{9}$/,
                        message: 'Please enter a valid 10-digit mobile number!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item>
                    <Button htmlType="submit">Submit</Button>
                    <Button type="link">Continue without Info</Button>
                  </Form.Item>
                </Form>
              </Modal>
            )}
            {orderInfoModal && orderInfoModal.visible && (
              <Modal
                // title="Patient & Order Information"
                open={orderInfoModal.visible}
                onCancel={closeOrdInfo}
                footer={[
                  <Button
                    key="cancel"
                    onClick={closeOrdInfo}
                  >
                    Cancel
                  </Button>,
                ]}
                width={'43.75rem'}
              >
                <OrderInfoPopup
                  data={orderInfoModal.data}
                  closeModal={closeOrdInfo}
                  refreshTable={() =>
                    filterResults(filters, pagination.current, pagination.pageSize)
                  }
                />
              </Modal>
            )}
            {viewerModal && (
              <Modal
                width={'62.5rem'}
                open={viewerModal?.visible}
                onCancel={() => {
                  setViewerModal({ visible: false });
                }}
                okButtonProps={{ style: { display: 'none' } }}
                style={{ top: '1.25rem' }}
                styles={{
                  body: {
                    height: '85vh',
                    maxHeight: '85vh',
                    overflow: 'hidden',
                    padding: 0,
                  },
                }}
              >
                <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                  <PdfViewer
                    order_id={viewerModal?.order_id}
                    patient_id={viewerModal?.patient_id}
                    pdfArrayBuffer={viewerModal?.data}
                    patientData={viewerModal?.patientData}
                    onReportUpdated={refetchPdf}
                    refreshing={pdfRefreshing}
                  />
                </div>
              </Modal>
            )}

            {/* Edit Signed-off Report Modal */}
            {editReportModal && editReportModal.visible && (
              <Modal
                title="Edit Signed-off Report"
                width={'62.5rem'}
                open={editReportModal.visible}
                onCancel={() => {
                  setEditReportModal({ visible: false, reportData: null, patientData: null });
                }}
                cancelText="Cancel"
                okText="Save"
                onOk={async () => {
                  try {
                    const response = await makePostCall('/submit-report', {
                      html: editReportModal.reportData?.pr_report_html,
                      yh_no: editReportModal.patientData?.pacs_order?.patient?.pat_pin,
                      order_no: editReportModal.patientData?.pacs_order?.po_his_ord_no,
                      acc_no: editReportModal.patientData?.pacs_order?.po_acc_no,
                      order_id: editReportModal.order_id,
                      user_id: getUserDetails()?.username,
                      status: 'signoff',
                      report_id: editReportModal.reportData?.pr_id,
                      is_editing_signedoff: true,
                      edited_at: new Date().toISOString(),
                      edited_by: getUserDetails()?.username,
                    });

                    if (response.data?.success || response.data?.data) {
                      message.success('Report updated successfully');
                      setEditReportModal({ visible: false, reportData: null, patientData: null });
                      filterResults(filters, pagination.current, pagination.pageSize);
                    } else {
                      message.error(response.data?.message || 'Failed to update report');
                    }
                  } catch (error) {
                    console.error('Error updating report:', error);
                    message.error(error.response?.data?.message || 'Failed to update report');
                  }
                }}
                style={{ top: '1.25rem' }}
                styles={{
                  body: {
                    height: '75vh',
                    maxHeight: '75vh',
                    overflow: 'hidden',
                    padding: 0,
                  },
                }}
              >
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <TinyEditor
                    initialContent={editReportModal.reportData?.pr_report_html || '<div></div>'}
                    placeholder="Edit report content..."
                    handleChange={newContent => {
                      setEditReportModal(prev => ({
                        ...prev,
                        reportData: {
                          ...prev.reportData,
                          pr_report_html: newContent,
                        },
                      }));
                    }}
                    fromReporting={false}
                    customHeight="75vh"
                  />
                </div>
              </Modal>
            )}
          </div>
          {selectedStudyForShare && (
            <WhatsAppShareModal
              visible={shareModalVisible}
              onCancel={() => {
                setShareModalVisible(false);
                setSelectedStudyForShare(null);
              }}
              studyUID={selectedStudyForShare?.ps_study_uid}
              studyDescription={selectedStudyForShare?.pacs_order?.po_diag_desc}
              pacOrdId={selectedStudyForShare?.pacs_order?.pacs_ord_id}
              patientName={selectedStudyForShare?.pacs_order?.patient?.pat_name}
              patientMobile={selectedStudyForShare?.pacs_order?.patient?.pat_mobile}
              patientGender={selectedStudyForShare?.pacs_order?.patient?.pat_sex}
              patientDOB={selectedStudyForShare?.pacs_order?.patient?.pat_dob}
              studyDate={selectedStudyForShare?.ps_study_dt_tm}
              refDoctorName={selectedStudyForShare?.pacs_order?.po_ref_doc}
              refDoctorContact={selectedStudyForShare?.pacs_order?.po_ref_doc_mobile}
            />
          )}

          {selectedStudyForDownload && (
            <DownloadStudyModal
              visible={downloadModalVisible}
              onCancel={() => {
                setDownloadModalVisible(false);
                setSelectedStudyForDownload(null);
              }}
              studyUID={selectedStudyForDownload?.ps_study_uid}
              studyDescription={selectedStudyForDownload?.pacs_order?.po_diag_desc}
              patientName={selectedStudyForDownload?.pacs_order?.patient?.pat_name}
              accessionNumber={selectedStudyForDownload?.pacs_order?.po_acc_no}
              studyData={selectedStudyForDownload}
            />
          )}

          {selectedStudyForEdit && (
            <EditStudyDetailsModal
              visible={editStudyModalVisible}
              onCancel={() => {
                setEditStudyModalVisible(false);
                setSelectedStudyForEdit(null);
              }}
              onSuccess={() => {
                // Refresh the data after successful update
                filterResults(filters, pagination.current, pagination.pageSize);
              }}
              studyData={{
                patientName: selectedStudyForEdit?.pacs_order?.patient?.pat_name || '',
                patientId: selectedStudyForEdit?.pacs_order?.patient?.pat_pin || '',
                patientAge: selectedStudyForEdit?.pacs_order?.patient?.pat_age || '',
                patientDOB: selectedStudyForEdit?.pacs_order?.patient?.pat_dob,
                patientGender: selectedStudyForEdit?.pacs_order?.patient?.pat_sex || '',
                studyDescription: selectedStudyForEdit?.pacs_order?.po_diag_desc || '',
                accessionNumber: selectedStudyForEdit?.pacs_order?.po_acc_no || '',
                referringPhysician: selectedStudyForEdit?.pacs_order?.po_ref_doc || '',
                history: selectedStudyForEdit?.pacs_order?.po_history || '',
                studyUID: selectedStudyForEdit?.ps_study_uid,
                orderId: selectedStudyForEdit?.pacs_order?.pacs_ord_id,
              }}
            />
          )}

          {selectedStudyForHistory && (
            <StudyHistoryDialog
              visible={studyHistoryModalVisible}
              orderId={selectedStudyForHistory?.pacs_order?.pacs_ord_id}
              onClose={() => {
                setStudyHistoryModalVisible(false);
                setSelectedStudyForHistory(null);
              }}
            />
          )}
        </div>
      </div>
    </Spin>
  );
};

export default PacsList;
